import React, { useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Switch } from './ui/switch';
import { toast } from 'sonner';
import { 
  MessageSquare, Send, Loader2, Phone, Clock, 
  CheckCircle2, XCircle, AlertCircle, FileText, RefreshCw,
  ArrowDownLeft, ArrowUpRight, Settings, Bell
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// SMS Composer for a specific lead
export const IntakeSMSComposer = ({ 
  leadId, 
  contactName, 
  contactPhone,
  onMessageSent 
}) => {
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showTemplates, setShowTemplates] = useState(false);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [settings, setSettings] = useState(null);

  const getToken = () => localStorage.getItem('token');

  const fetchSettings = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/intake/sms/settings`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSettings(data);
      }
    } catch (error) {
      console.error('Error fetching SMS settings:', error);
    }
  }, []);

  const fetchTemplates = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/intake/sms/templates`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (response.ok) {
        const data = await response.json();
        setTemplates(data.templates || []);
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  }, []);

  const fetchHistory = useCallback(async () => {
    if (!leadId) return;
    
    setLoadingHistory(true);
    try {
      const response = await fetch(`${API_URL}/api/intake/sms/messages/${leadId}`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (response.ok) {
        const data = await response.json();
        setHistory(data.messages || []);
      }
    } catch (error) {
      console.error('Error fetching SMS history:', error);
    } finally {
      setLoadingHistory(false);
    }
  }, [leadId]);

  useEffect(() => {
    fetchSettings();
    fetchTemplates();
    fetchHistory();
  }, [fetchSettings, fetchTemplates, fetchHistory]);

  const handleSend = async () => {
    if (!message.trim()) {
      toast.error('Please enter a message');
      return;
    }

    setSending(true);
    try {
      const formData = new FormData();
      formData.append('message', message.trim());
      if (selectedTemplate?.id) {
        formData.append('template_id', selectedTemplate.id);
      }

      const response = await fetch(`${API_URL}/api/intake/sms/send/${leadId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${getToken()}` },
        body: formData
      });

      const data = await response.json();

      if (response.ok && data.success) {
        toast.success(data.demo_mode ? 'SMS queued (demo mode)' : 'SMS sent successfully');
        setMessage('');
        setSelectedTemplate(null);
        fetchHistory();
        if (onMessageSent) onMessageSent(data);
      } else {
        toast.error(data.detail || 'Failed to send SMS');
      }
    } catch (error) {
      toast.error('Failed to send SMS');
    } finally {
      setSending(false);
    }
  };

  const applyTemplate = (template) => {
    let content = template.content;
    if (contactName) {
      content = content.replace('{{client_name}}', contactName);
    }
    setMessage(content);
    setSelectedTemplate(template);
    setShowTemplates(false);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'delivered': return <CheckCircle2 className="w-3 h-3 text-green-500" />;
      case 'sent': return <CheckCircle2 className="w-3 h-3 text-blue-500" />;
      case 'failed': return <XCircle className="w-3 h-3 text-red-500" />;
      default: return <Clock className="w-3 h-3 text-amber-500" />;
    }
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  const charCount = message.length;

  return (
    <Card className="border-emerald-200">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-emerald-600" />
            <CardTitle className="text-base">SMS to Prospect</CardTitle>
          </div>
          {settings && !settings.twilio_configured && (
            <Badge variant="outline" className="text-amber-600 border-amber-300 bg-amber-50">
              <AlertCircle className="w-3 h-3 mr-1" />
              Demo Mode
            </Badge>
          )}
        </div>
        {contactPhone && (
          <CardDescription className="flex items-center gap-1">
            <Phone className="w-3 h-3" />
            {contactPhone}
          </CardDescription>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        {!contactPhone ? (
          <div className="text-center py-4 text-muted-foreground">
            <Phone className="w-8 h-8 mx-auto mb-2 opacity-30" />
            <p className="text-sm">No phone number for this contact</p>
          </div>
        ) : (
          <>
            {/* Message Composer */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Message</Label>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowTemplates(true)}
                  className="text-xs"
                >
                  <FileText className="w-3 h-3 mr-1" />
                  Templates
                </Button>
              </div>
              <Textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Type your message..."
                rows={3}
                maxLength={1600}
                data-testid="intake-sms-message"
              />
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>{charCount}/1600</span>
                {selectedTemplate && (
                  <Badge variant="secondary" className="text-xs">
                    {selectedTemplate.name}
                  </Badge>
                )}
              </div>
            </div>

            {/* Send Button */}
            <Button
              onClick={handleSend}
              disabled={sending || !message.trim()}
              className="w-full bg-emerald-600 hover:bg-emerald-700"
              data-testid="intake-sms-send-btn"
            >
              {sending ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              Send SMS
            </Button>

            {/* Message History */}
            {history.length > 0 && (
              <div className="border-t pt-4 mt-4">
                <div className="flex items-center justify-between mb-3">
                  <Label className="text-sm font-medium flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    Message History
                  </Label>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={fetchHistory}
                    disabled={loadingHistory}
                  >
                    <RefreshCw className={`w-3 h-3 ${loadingHistory ? 'animate-spin' : ''}`} />
                  </Button>
                </div>
                
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {history.map((msg) => (
                    <div
                      key={msg.id}
                      className={`p-2 rounded-lg text-sm ${
                        msg.direction === 'outbound' ? 'bg-emerald-50 ml-4' : 'bg-gray-100 mr-4'
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-1 text-xs text-muted-foreground">
                        {msg.direction === 'outbound' ? (
                          <ArrowUpRight className="w-3 h-3 text-emerald-500" />
                        ) : (
                          <ArrowDownLeft className="w-3 h-3 text-blue-500" />
                        )}
                        <span>{formatTime(msg.created_at)}</span>
                        <span className="ml-auto flex items-center gap-1">
                          {getStatusIcon(msg.status)}
                          {msg.status}
                        </span>
                      </div>
                      <p className="text-gray-700">{msg.message}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>

      {/* Templates Dialog */}
      <Dialog open={showTemplates} onOpenChange={setShowTemplates}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Intake SMS Templates</DialogTitle>
            <DialogDescription>
              Select a template for your message
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {templates.map((template) => (
              <div
                key={template.id}
                className="p-3 border rounded-lg hover:bg-slate-50 cursor-pointer"
                onClick={() => applyTemplate(template)}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="font-medium text-sm">{template.name}</span>
                  <Badge variant="secondary" className="text-xs">
                    {template.category}
                  </Badge>
                </div>
                <p className="text-sm text-muted-foreground line-clamp-2">
                  {template.content}
                </p>
              </div>
            ))}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTemplates(false)}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

// SMS Settings for Intake.AI
export const IntakeSMSSettings = () => {
  const [settings, setSettings] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showTemplateDialog, setShowTemplateDialog] = useState(false);
  const [templateData, setTemplateData] = useState({ name: '', content: '', category: 'general' });

  const getToken = () => localStorage.getItem('token');

  const stages = [
    { key: 'contacted', label: 'Contacted' },
    { key: 'consultation_scheduled', label: 'Consultation Scheduled' },
    { key: 'consultation_complete', label: 'Consultation Complete' },
    { key: 'agreement_sent', label: 'Agreement Sent' },
    { key: 'signed_converted', label: 'Signed/Converted' }
  ];

  const fetchSettings = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/intake/sms/settings`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSettings(data);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  }, []);

  const fetchTemplates = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/intake/sms/templates`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (response.ok) {
        const data = await response.json();
        setTemplates(data.templates || []);
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
    fetchTemplates();
  }, [fetchSettings, fetchTemplates]);

  const handleToggleNotifications = async (enabled) => {
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/intake/sms/settings?sms_notifications_enabled=${enabled}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (response.ok) {
        setSettings({ ...settings, sms_notifications_enabled: enabled });
        toast.success(enabled ? 'SMS notifications enabled' : 'SMS notifications disabled');
      }
    } catch (error) {
      toast.error('Failed to update settings');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleStage = async (stage, enabled) => {
    const newStages = { ...(settings?.sms_notification_stages || {}), [stage]: enabled };
    
    try {
      const response = await fetch(`${API_URL}/api/intake/sms/settings?sms_notifications_enabled=${settings?.sms_notifications_enabled || false}&sms_notification_stages=${encodeURIComponent(JSON.stringify(newStages))}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (response.ok) {
        setSettings({ ...settings, sms_notification_stages: newStages });
      }
    } catch (error) {
      toast.error('Failed to update stage setting');
    }
  };

  const handleCreateTemplate = async () => {
    if (!templateData.name || !templateData.content) {
      toast.error('Name and content are required');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('name', templateData.name);
      formData.append('content', templateData.content);
      formData.append('category', templateData.category);

      const response = await fetch(`${API_URL}/api/intake/sms/templates`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${getToken()}` },
        body: formData
      });

      if (response.ok) {
        toast.success('Template created');
        setShowTemplateDialog(false);
        setTemplateData({ name: '', content: '', category: 'general' });
        fetchTemplates();
      }
    } catch (error) {
      toast.error('Failed to create template');
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!confirm('Delete this template?')) return;

    try {
      const response = await fetch(`${API_URL}/api/intake/sms/templates/${templateId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });

      if (response.ok) {
        toast.success('Template deleted');
        fetchTemplates();
      } else {
        toast.error('Cannot delete default template');
      }
    } catch (error) {
      toast.error('Failed to delete template');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <Loader2 className="w-5 h-5 animate-spin mr-2" />
        <span>Loading SMS settings...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Twilio Status */}
      <Card className={settings?.twilio_configured ? 'border-green-200 bg-green-50/50' : 'border-amber-200 bg-amber-50/50'}>
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Phone className={`w-5 h-5 ${settings?.twilio_configured ? 'text-green-600' : 'text-amber-600'}`} />
              <div>
                <p className="font-medium">Twilio SMS</p>
                <p className="text-sm text-muted-foreground">
                  {settings?.twilio_configured ? settings.twilio_phone_number : 'Not configured'}
                </p>
              </div>
            </div>
            <Badge className={settings?.twilio_configured ? 'bg-green-500' : 'bg-amber-500'}>
              {settings?.twilio_configured ? 'Active' : 'Demo Mode'}
            </Badge>
          </div>
        </CardContent>
      </Card>

      {/* Auto Notifications */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                <Bell className="w-5 h-5 text-emerald-600" />
                Auto SMS Notifications
              </CardTitle>
              <CardDescription>
                Automatically send SMS when leads move to new stages
              </CardDescription>
            </div>
            <Switch
              checked={settings?.sms_notifications_enabled || false}
              onCheckedChange={handleToggleNotifications}
              disabled={saving}
            />
          </div>
        </CardHeader>
        {settings?.sms_notifications_enabled && (
          <CardContent>
            <div className="space-y-3">
              {stages.map((stage) => (
                <div key={stage.key} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <span className="text-sm font-medium">{stage.label}</span>
                  <Switch
                    checked={settings?.sms_notification_stages?.[stage.key] || false}
                    onCheckedChange={(checked) => handleToggleStage(stage.key, checked)}
                  />
                </div>
              ))}
            </div>
          </CardContent>
        )}
      </Card>

      {/* Templates */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                <FileText className="w-5 h-5 text-emerald-600" />
                Intake SMS Templates
              </CardTitle>
              <CardDescription>
                Reusable templates for prospect communications
              </CardDescription>
            </div>
            <Button size="sm" onClick={() => setShowTemplateDialog(true)}>
              + Add Template
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {templates.map((template) => (
              <div key={template.id} className="flex items-start justify-between p-3 bg-slate-50 rounded-lg">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-medium text-sm">{template.name}</span>
                    {template.is_default && <Badge variant="outline" className="text-xs">Default</Badge>}
                  </div>
                  <p className="text-sm text-muted-foreground line-clamp-2">{template.content}</p>
                </div>
                {!template.is_default && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-red-500"
                    onClick={() => handleDeleteTemplate(template.id)}
                  >
                    ×
                  </Button>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Create Template Dialog */}
      <Dialog open={showTemplateDialog} onOpenChange={setShowTemplateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New Intake SMS Template</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Template Name</Label>
              <Input
                value={templateData.name}
                onChange={(e) => setTemplateData({ ...templateData, name: e.target.value })}
                placeholder="e.g., Welcome Message"
              />
            </div>
            <div className="space-y-2">
              <Label>Category</Label>
              <Select
                value={templateData.category}
                onValueChange={(v) => setTemplateData({ ...templateData, category: v })}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="initial">Initial Contact</SelectItem>
                  <SelectItem value="reminder">Reminder</SelectItem>
                  <SelectItem value="followup">Follow-up</SelectItem>
                  <SelectItem value="document">Document Request</SelectItem>
                  <SelectItem value="agreement">Agreement</SelectItem>
                  <SelectItem value="general">General</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Message Content</Label>
              <Textarea
                value={templateData.content}
                onChange={(e) => setTemplateData({ ...templateData, content: e.target.value })}
                placeholder="Hi {{client_name}}, ..."
                rows={4}
              />
              <p className="text-xs text-muted-foreground">
                Variables: {'{{client_name}}'}, {'{{case_type}}'}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTemplateDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateTemplate}>Create Template</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default IntakeSMSComposer;
